import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useIntl } from "gatsby-plugin-react-intl"

import ImageContentful from "./ImageContentful"
import Link from "./Link"
import { media } from "../styles"

export const SubNavStyles = css`
  font-size: 15px;
  font-weight: 400;
  margin: 0;
  text-transform: none;
  transform: translate3d(0, 0, 0);
`

export const NavGrid = styled.ul`
  ${SubNavStyles}
  display: flex;
  flex-flow: row wrap;
  font-size: 15px;
  font-weight: 700;
  gap: 10px;
  letter-spacing: -0.07px;
  line-height: 1.2;
  list-style: none;
  margin: 0 auto 10px;
  padding: 0;
  text-transform: none;

  @media ${media.lg} {
    gap: 20px;
  }

  ${(props) =>
    props.$mode === "crossnav" &&
    css`
      justify-content: center;
      gap: 20px;
    `}

  &:last-child {
    margin-bottom: 0;
  }
`

export const NavGridItem = styled.li`
  display: flex;
  font-size: 15px;
  font-weight: 400;
  margin: 0;
  padding: 0;
  width: 100%;

  ${(props) =>
    props.$mode === "crossnav" &&
    css`
      width: auto;
    `}

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${({ theme }) => theme.spaceblue};
    display: block;
    padding: 0;
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitionTime};
    white-space: nowrap;
    width: 100%;

    > span {
      align-items: center;
      display: flex;
      flex-flow: row nowrap;
    }

    &:hover {
      color: ${({ theme }) => theme.sun} !important;
    }
  }

  ${(props) =>
    props.$mode === "crossnav" &&
    css`
      a {
        background-color: ${({ theme }) => theme.white};
        border-radius: 25px;
        padding: 25px;
        min-width: 205px;
      }
    `}
`

export const NavGridIcon = styled.div`
  align-items: center;
  aspect-ratio: 1;
  color: ${(props) =>
    props.$color ? props.theme[props.$color] : props.theme.spaceblue};
  background: ${({ theme }) => theme.greyLight};
  border-radius: 10px;
  display: flex;
  height: 48px;
  justify-content: center;
  margin: 0 10px 0 0;
  width: 48px;

  .wrap--svg,
  svg,
  img {
    display: block;
    height: 24px;
    width: 24px;
    margin: 0 !important;
  }

  ${(props) =>
    props.$hasLarge &&
    css`
      @media ${media.xl} {
        display: none;
      }
    `}
`

const NavigationGrid = ({
  basePath,
  iconColor,
  items,
  mode,
  handleFocus,
  ...props
}) => {
  const intl = useIntl()
  return (
    <NavGrid $mode={mode} {...props}>
      {items?.map((item) => (
        <NavGridItem key={item.id} $mode={mode}>
          <Link
            intl={false}
            onFocus={handleFocus ? () => handleFocus() : null}
            to={`/${intl.locale}${basePath || "/"}${item.slug}/`}
          >
            {item.icon ? (
              <NavGridIcon aria-hidden="true" $color={iconColor}>
                <ImageContentful image={item.icon} alt={item.name} inline />
              </NavGridIcon>
            ) : null}
            {item.name}
          </Link>
        </NavGridItem>
      ))}
      {items.length > 1 && items?.length % 2 === 1 ? (
        <NavGridItem $isHidden={mode === "crossnav"} $mode={mode}></NavGridItem>
      ) : null}
    </NavGrid>
  )
}

NavigationGrid.propTypes = {
  basePath: PropTypes.string,
  handleFocus: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.object,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      node_locale: PropTypes.string,
    })
  ).isRequired,
  mode: PropTypes.oneOf(["crossnav", "topnav"]),
  iconColor: PropTypes.string,
}

export default NavigationGrid
